import { useEffect } from 'react'
import { MutationUpdaterFn } from '@apollo/client'

import {
  MakePlanPrimaryMutation,
  PlansByOwnerDocument,
  PlansByOwnerQuery,
  useMakePlanPrimaryMutation,
  usePlansByOwnerQuery
} from '../../../../generated/graphql'
import { PlanOption } from '../../../../types'
import { ConfirmReplacePrimaryModal } from './ConfirmReplacePrimaryModal'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'

type ConfirmReplacePrimaryModalWrapperProps = {
  /** The text for the modal body */
  bodyText?: string
  /** The text of the confirm button */
  confirmText?: string
  /** ID of the Plan */
  id: string
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when modal is closed (Escape, Cancel, or X button) - A11Y-535: Required for focus restoration */
  onClose?: () => void
  /** Event callback when when the mutation has completed */
  onMutationCompleted?: (id: string) => void
  /** Owner AppUserId. Used by Student Overview Plans */
  ownerAppUserId: number
  /** The title of the modal */
  title?: string
  /** Flag for whether or not to update the cache */
  updateCache?: boolean
}

const ConfirmReplacePrimaryModalWrapper = ({
  bodyText = 'Choose another plan as your primary plan:',
  confirmText = 'Ok',
  id,
  isOpen,
  onClose,
  onMutationCompleted,
  ownerAppUserId,
  title = 'Remove as Primary Plan',
  updateCache = true
}: ConfirmReplacePrimaryModalWrapperProps) => {
  const query = usePlansByOwnerQuery({
    variables: { ownerAppUserId },
    onError: () => {
      showErrorNotif('An error occurred. Please refresh the page.')
    }
  })
  const { hideModal } = useModal()
  const { showErrorNotif, showSuccessNotif } = useNotification()
  const update: MutationUpdaterFn<MakePlanPrimaryMutation> = (cache, { data }) => {
    // Apollo will automatically update the cache for the current object
    // but you can only have one primary plan and Apollo doesn't know that
    // so we need to update all the plans in cache to reflect that
    const makePlanPrimaryStatus = data?.makePlanPrimary

    if (makePlanPrimaryStatus?.success && updateCache) {
      // TODO: we could avoid this readQuery if the mutation response
      // returned the old primary plan id
      const queryCache = cache.readQuery<PlansByOwnerQuery>({
        query: PlansByOwnerDocument,
        variables: { ownerAppUserId }
      })

      if (queryCache) {
        const cachePlans = queryCache.plans.map((p) => ({
          ...p,
          isPrimary: p.id === makePlanPrimaryStatus.id
        }))
        cache.writeQuery({
          query: PlansByOwnerDocument,
          data: { plans: cachePlans },
          variables: { ownerAppUserId }
        })
      }

      // update the primaryPlan singleton
      cache.modify({
        id: 'ROOT_QUERY',
        fields: {
          primaryPlan(_old, { toReference }) {
            return toReference(makePlanPrimaryStatus.id)
          }
        }
      })
    }
  }
  const onCompleted = (data: MakePlanPrimaryMutation) => {
    if (!data.makePlanPrimary.success) {
      showErrorNotif('Could not update primary plan.')
      hideModal()
      return
    }

    if (onMutationCompleted) {
      onMutationCompleted(data.makePlanPrimary.id)
    } else {
      const newPrimaryTitle = query.data?.plans.find((p) => p.id === data.makePlanPrimary.id)?.title
      showSuccessNotif(
        newPrimaryTitle ? `${newPrimaryTitle} is now the primary plan.` : 'Primary plan updated.'
      )
      hideModal()
    }
  }

  const [makePlanPrimary, mutation] = useMakePlanPrimaryMutation({
    update,
    onCompleted,
    onError: () => {
      showErrorNotif('Could not update primary plan.', makePlanPrimary)
    }
  })

  const onConfirm = (planId: string) => {
    makePlanPrimary({
      variables: {
        input: planId
      },
      optimisticResponse: {
        __typename: 'Mutation',
        makePlanPrimary: {
          __typename: 'ResponseStatus',
          id: planId,
          message: '',
          success: true
        }
      }
    })
  }

  // A11Y-535: Handle modal close to restore focus
  const onCloseModal = () => {
    hideModal()
    if (onClose) {
      onClose()
    }
  }

  useEffect(() => {
    // If this plan is not primary, mark it as primary; no need to show the modal
    if (
      query.data &&
      query.data.plans &&
      !query.data.plans.find((p) => p.id === id)?.isPrimary &&
      !mutation.called
    ) {
      onConfirm(id)
    }
  })

  // Determine if the current plan is already primary, and get the other plans to show in the drop down
  let planIsPrimary = false
  let filteredPlans: PlanOption[] = []
  if (query.data && query.data.plans) {
    planIsPrimary = query.data.plans.find((p) => p.id === id)?.isPrimary!
    filteredPlans = query.data.plans
      .filter((p) => p.id !== id)
      ?.map((p) => ({ id: p.id, label: p.title }))
  }

  // Only show the dialog if the current plan is primary
  // and the mutation for making it primary hasn't already been called
  return planIsPrimary && !mutation.called ? (
    <ConfirmReplacePrimaryModal
      bodyText={bodyText!}
      confirmText={confirmText!}
      isOpen={isOpen}
      onClose={onCloseModal}
      onConfirm={onConfirm}
      planOptions={filteredPlans}
      title={title!}
    />
  ) : null
}

export default ConfirmReplacePrimaryModalWrapper
